import { FC, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { CheckCircle as CheckCircleIcon } from "@material-ui/icons";
import { usePostHog } from "posthog-js/react";

import {
  AllAccessDiscountTooltip,
  Button,
  ErrorMessage,
  LabelField,
  Modal,
  ROIEquation,
} from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { useTimer } from "hooks";
import { getReturnOnInvestment } from "lib/finance";
import {
  formatCurrencyAmount,
  formatDateRange,
  formatPercentage,
} from "lib/format";
import { goToListingDealPage } from "lib/helpers";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { ResaleInfo } from "services/listings/types";
import { acceptListingOffer } from "services/offers";
import { ListingOffer } from "services/offers/types";
import theme from "theme";
import { circleIconSize } from "theme/constants";

import TransactionFeeNotice from "../TransactionFeeNotice";

interface AcceptOfferModalProps {
  isAcceptingFinalOffersEnabled: boolean;
  isOwner: boolean;
  listingId: number;
  offer: ListingOffer;
  onClose: () => void;
  resaleInfo: ResaleInfo;
  royxTake: number;
}

const AcceptOfferModal: FC<AcceptOfferModalProps> = ({
  isAcceptingFinalOffersEnabled,
  isOwner,
  listingId,
  offer,
  onClose,
  resaleInfo,
  royxTake,
}) => {
  const currentTime = useTimer();

  const { handleRefreshListing, listing } = useAssetDetailData(listingId);
  const posthog = usePostHog();

  const [step, setStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isCounteroffer = !!offer.countered_offer_id;
  const netProceeds = Number(offer.amount) * (1 - royxTake);

  const titleByStep = [
    `Accept ${isCounteroffer ? "Counteroffer" : "Offer"}`,
    `${isCounteroffer ? "Counteroffer" : "Offer"} Accepted!`,
  ];

  const handleClose = () => {
    onClose();
    setTimeout(() => setStep(0), 100);
  };

  const handleAccept = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      await handleRefreshListing(); // Verify listing is up to date
      await acceptListingOffer(offer, listingId, posthog);
      setStep(s => s + 1);
      if (!isAcceptingFinalOffersEnabled) {
        setTimeout(() => goToListingDealPage(offer.id), 3000);
      }
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal title={titleByStep[step]} open onClose={handleClose}>
      {step === 0 && (
        <Box my={2}>
          {!!isOwner ? (
            <Typography component="div" gutterBottom>
              <Box fontSize={13}>
                {isAcceptingFinalOffersEnabled
                  ? offer.kind === "seller_counteroffer"
                    ? "Accepting this counteroffer will begin a 24-hour auction for this asset starting at the counteroffer amount. If no other investors bid, you will win the asset at this price. You will have the opportunity to bid again during the final auction."
                    : offer.kind === "buyer_counteroffer"
                    ? "Accepting this counteroffer will begin a 24-hour auction where buyers will have a final chance to bid on your asset. If no other bids are made during the auction, you agree to sell this asset for this amount."
                    : "Accept this offer to begin a 24-hour auction where buyers will have a final chance to bid on your asset. If no other bids are made during the auction, you agree to sell this asset for this amount."
                  : "Accept this offer and you'll be paid once the assignment is confirmed with your royalty payor. Once an offer expires you may not accept it."}
              </Box>
            </Typography>
          ) : (
            <Typography component="div" gutterBottom>
              <Box fontSize={13}>
                {isAcceptingFinalOffersEnabled
                  ? "Accepting this counteroffer will begin a 24-hour auction for this asset starting at the counteroffer amount. If no other investors bid, you will win this asset at this price. You will have the opportunity to bid again during the final auction."
                  : ""}
              </Box>
            </Typography>
          )}
          {errorMessage && (
            <Box my={2}>
              <ErrorMessage title="Request Failed" description={errorMessage} />
            </Box>
          )}
          <Box my={2} textAlign="center">
            <Typography component="div">
              <Box my={2}>
                <LabelField
                  label={
                    isCounteroffer && !isOwner
                      ? "Seller's Counteroffer Expires"
                      : isCounteroffer
                      ? "Counteroffer Expires"
                      : "Offer Expires"
                  }
                  value={`In ${formatDateRange(
                    currentTime,
                    offer.expiration,
                    2,
                  )}`}
                />
              </Box>
              <Box my={2}>
                <LabelField
                  label={`Offer Amount ${!!isOwner ? "(Buyer Pays)" : ""}`}
                  value={formatCurrencyAmount("USD", offer.amount)}
                />
              </Box>
              {!!isOwner && (
                <>
                  <Box my={2}>
                    <LabelField
                      label={`\u2212 Marketplace Fee (${formatPercentage(
                        royxTake,
                      )})`}
                      value={
                        isAcceptingFinalOffersEnabled
                          ? `\u2212 ${formatPercentage(royxTake)}`
                          : `\u2212${formatCurrencyAmount(
                              "USD",
                              Number(offer.amount) * royxTake,
                            )}`
                      }
                      tooltipText={
                        listing?.kind !== "direct_listing" ? (
                          <AllAccessDiscountTooltip />
                        ) : undefined
                      }
                      interactiveTooltip
                    />
                  </Box>
                  <Box my={2}>
                    <LabelField
                      label={`= ${
                        isAcceptingFinalOffersEnabled ? "Minimum" : "Your"
                      } Sale Proceeds`}
                      value={formatCurrencyAmount("USD", netProceeds)}
                    />
                  </Box>
                  {!!resaleInfo && !!resaleInfo?.last_update && (
                    <Box my={2}>
                      <LabelField
                        label={`${
                          isAcceptingFinalOffersEnabled ? "Minimum " : ""
                        }Return On Investment`}
                        tooltipText={<ROIEquation />}
                        value={formatPercentage(
                          getReturnOnInvestment(
                            resaleInfo.royalties_received,
                            netProceeds,
                            resaleInfo.purchased_amount,
                            resaleInfo.buyer_fee,
                          ),
                        )}
                      />
                    </Box>
                  )}
                </>
              )}
            </Typography>
          </Box>
          {!isOwner && (
            <TransactionFeeNotice isPurchasing price={Number(offer.amount)} />
          )}
          <Box my={2}>
            <Button
              customColor="green"
              variant="contained"
              size="large"
              fullWidth
              onClick={handleAccept}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Accept {isCounteroffer ? "Counteroffer" : "Offer"}
            </Button>
          </Box>
        </Box>
      )}
      {step === 1 && (
        <Typography component="div">
          <Box textAlign="center" m={2}>
            <CheckCircleIcon
              style={{
                fontSize: circleIconSize.big,
                color: theme.custom.palette.green.main,
              }}
            />
          </Box>
          <Box fontSize={15} textAlign="center" m={2}>
            {isAcceptingFinalOffersEnabled
              ? "The 24-hour final auction has begun."
              : "You'll be redirected to a page with the closing process shortly."}
          </Box>
        </Typography>
      )}
    </Modal>
  );
};

export default AcceptOfferModal;
